import { IStringifyOptions, stringify } from 'qs'

/**
 * The DeepQueryObject can be as recursively deep as needed.
 * When used as an input to `queryString`, the deep object can
 * end in any of the types described in `QueryObjectValues`.
 * After parsing, we guarantee that the object has been santized
 * down to only contain a single string at the end of the nesting
 * to meet API conventions
 */
type DeepQueryObject<T> = {
  [key: string]: DeepQueryObject<T> | T
}

type QueryObjectValues =
  | string
  | number
  | boolean
  | null
  | Array<string | number>

const arrayToCsv = (arr: Array<string | number>): string => arr.join(',')

/**
 * Parses deep query parameters into a format that the `qs.stringify`
 * utility can parse into a string friendly to our APIs.
 *
 * e.g.
 *  input: { a: { b: ['foo', 'bar' ] } }
 *  output: { a: { b: 'foo,bar' } }
 */
const recursivelyFlattenDeepObjects = (
  obj: DeepQueryObject<QueryObjectValues> | QueryObjectValues
): DeepQueryObject<string> | string | boolean => {
  /**
   * If `obj` is a string, Array<string>, number, or Array<number>,
   * we've hit the end of our recursion. Convert to string
   * and return the value.
   */
  if (typeof obj === 'string' || typeof obj === 'boolean') return obj
  if (typeof obj === 'number') return obj.toString()
  if (Array.isArray(obj)) return arrayToCsv(obj)

  /**
   * If `obj` has made it this far, there's more recursion
   * to do. Call this function for each property in the object.
   */
  obj = Object.assign({}, obj)
  for (const prop in obj) {
    obj[prop] = recursivelyFlattenDeepObjects(obj[prop])
  }

  /**
   *  Using a type cast to DeepQueryObject<string> due to the recursive
   *  call above - we're ensuring that by the time we return `obj`, the object
   *  has been mutated to remove non-string values at the end of the tree.
   */
  return obj as DeepQueryObject<string>
}

export type QueryParams =
  | {
      fields?: Record<string, Array<string> | string>
      filter?: DeepQueryObject<QueryObjectValues>
      include?: Array<string>
      page?: {
        limit?: number
        offset?: number
      }
      sort?: string | Array<string>
    }
  | Record<string, string>

/**
 * @note `queryString` is not compatible with v2 operations that allow the `page` query parameter to be a string, rather
 * than an object with `limit` and `offset` properties. For those operations you'll need to manually construct a type
 * that satisfies `QueryParams` (see `v2/labels/read.ts` `LabelReadParams`).
 */
export const queryString = (
  query?: QueryParams,
  opts?: IStringifyOptions
): string => {
  if (query === undefined) return ''

  const sort = Array.isArray(query.sort) ? arrayToCsv(query.sort) : query.sort

  const include = Array.isArray(query.include)
    ? arrayToCsv(query.include)
    : query.include

  const filter =
    query.filter !== undefined
      ? recursivelyFlattenDeepObjects(query.filter)
      : undefined

  const fields =
    query.fields !== undefined
      ? recursivelyFlattenDeepObjects(query.fields)
      : undefined

  const parsedQuery = {
    ...query,
    fields,
    filter,
    include,
    sort,
  }

  return stringify(parsedQuery, { arrayFormat: 'brackets', ...opts })
}
